<script setup>
import DOMPurify from 'dompurify';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import { helpers } from '@shop/frontend/web/js/helpers';
import { ref, computed, watch } from 'vue';
import { TEAM_ICON_URL } from '@shop/frontend/web/js/constants/event';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import 'dayjs/locale/en';

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  isShort: {
    type: Boolean,
    default: false,
  },
  isPerfomance: {
    type: Boolean,
    default: false,
  },
  isPackage: {
    type: Boolean,
    default: false,
  },
  isPackageItem: {
    type: Boolean,
    default: false,
  },
  linkOverride: {
    type: String,
    default: null,
  },
});

const eventLink = computed(() => props.linkOverride || props.event.pageLink);

const lang = ref($('html').attr('lang') || 'ru');

dayjs.locale(lang.value);

const showDescription = ref(false);

const matchesInfoText = computed(() => {
  const matchesCorrectForm = helpers.wordEnding(props.event.eventsCount, [
    translator.t('event', 'match'),
    translator.t('event', 'matches'),
    translator.t('event', 'matches '),
  ]);

  return `${props.event.eventsCount} ${matchesCorrectForm}`;
});

const sanitizedEventDescription = computed(() => DOMPurify.sanitize(props.event.description));
</script>

<template>
  <div>
    <div
      class="i-event-item"
      :class="{
        'i-event-item_short': isShort,
        'i-event-item-info_perfomance' : isPerfomance,
        'i-event-item_soon': event.settings && event.settings.isSoon,
      }"
    >
      <div v-if="event.settings.isShowSeason && !isPackageItem" class="i-event-item__top">
        {{ event.nom.name }}
      </div>
      <div
        class="i-event-item__wrapper"
        :class="{ 'i-event-item__wrapper_perfomance' : isPerfomance }"
      >
        <div
          v-if="!isShort"
          class="i-event-item-info"
          :class="{
            'i-event-item-info_perfomance' : isPerfomance,
            'i-event-item-info_home-logo': !event.settings.isHomeLogoWithOpponent
          }"
        >
          <slot name="info">
            <div
              v-if="event.eventsCount && isPackage"
              class="i-event-item-info__matches"
            >
              {{ matchesInfoText }}
            </div>

            <div
              v-if="event.settings.isShowDate && !isPackage"
              class="d-inline-flex align-items-center i-event-item-info__date text-primary"
            >
              <svg
                class="icon i-icon_stroke-color i-icon_sm mr-2"
                width="15"
                height="15"
              >
                <use xlink:href="#calendar" />
              </svg>
              <span class="text-lowercase">
                {{ dayjs(event.startTimestampStr).format("DD MMMM YYYY") }}
              </span>
              <span v-if="lang==='ru'" class="text-capitalize">
                &nbsp;{{ dayjs(event.startTimestampStr).format("dd HH:mm") }}
              </span>
              <span v-else-if="lang==='en'" class="text-capitalize">
                &nbsp;{{ dayjs(event.startTimestampStr).format("dd h:mm A") }}
              </span>
            </div>

            <div
              v-if="event.settings.isShowUnit && event.eventType != 'Пакет'"
              class="d-inline-flex align-items-center i-event-item-info__text"
            >
              <svg
                class="icon icon__location i-icon_stroke-color mr-2"
                width="16"
                height="16"
              >
                <use xlink:href="#location" />
              </svg>
              <span>
                {{ event.unitName }}
              </span>
            </div>
          </slot>
        </div>
        <slot name="name">
          <component
            :is="!event.settings.isSoon ? 'a' : 'div'"
            v-if="event.participants && event.participants.singular"
            v-bind="!event.settings.isSoon ? { href: eventLink } : {}"
            class="i-event-item-logo-name"
          >
            <div class="i-event-item__logo">
              <img
                :src="event.participants.singular.imageUrl || TEAM_ICON_URL"
                alt=""
                width="50"
                height="50"
              >
            </div>
            <span class="i-event-item__name">
              {{ event.participants.singular.name }}
            </span>
          </component>
          <component
            :is="!event.settings.isSoon ? 'a' : 'div'"
            v-else-if="isPackage"
            v-bind="!event.settings.isSoon ? { href: eventLink } : {}"
            class="i-event-item-logo-name"
          >
            <div class="i-event-item__logo">
              <img
                :src="event.packageEvents[0]?.participants.team.homeTeam.imageUrl || TEAM_ICON_URL"
                alt=""
                width="50"
                height="50"
              >
            </div>
            <div class="i-event-item__package-name-wrapper">
              <span class="i-event-item__name_package">
                {{ event.name }}
              </span>
              <span v-if="event?.nomName" class="i-event-item__nom-name">
                {{ event.nomName }}
              </span>
            </div>
          </component>
        </slot>
        <div class="i-event-item-actions">
          <slot name="actions">
            <button
              v-if="(event.description || event.eventType === 'Пакет') && !isPackageItem"
              type="button"
              class="i-btn i-btn_outlined i-event-item-actions__btn-more"
              @click="showDescription = !showDescription"
            >
              {{
                showDescription
                  ? translator.t("main", "Hide")
                  : translator.t("main", "More details")
              }}
            </button>

            <a
              v-if="event.settings && event.settings.parkingId && !isPackageItem"
              :href="'/event?id_event=' + event.settings.parkingId"
              class="i-btn i-btn_outlined i-event-item-actions__parking js-hover-out"
            >
              <svg
                width="20"
                height="20"
                class="icon"
                aria-hidden="true"
                focusable="false"
              >
                <use xlink:href="#parking" />
              </svg>
              <span class="sr-only">
                {{ translator.t("main", "Go to the event page") }}
              </span>
            </a>

            <a
              v-if="!(event.settings && event.settings.isSoon) && !isPackageItem"
              :href="eventLink"
              :title="translator.t('main', 'Go to the event page')"
              class="i-btn i-event-item-actions__btn-buy js-hover-out"
            >
              <svg
                width="20"
                height="20"
                class="icon icon_cart"
                aria-hidden="true"
                focusable="false"
              >
                <use xlink:href="#cart" />
              </svg>
              <span class="sr-only">
                {{ translator.t("main", "Go to the event page") }}
              </span>
            </a>
            <div
              v-if="event.settings && event.settings.isSoon && !isPackageItem"
              class="i-btn i-btn_outlined i-event-item__soon-block"
            >
              {{
                translator.t(
                  "main",
                  (event.settings && event.settings.isSoonText) || "Soon"
                )
              }}
            </div>
          </slot>
        </div>
      </div>
      <slot
        v-if="showDescription && isPackage"
        name="package"
      />
      <div
        v-if="event.description && showDescription"
        class="i-event-item-expand"
        :class="{ 'd-block': showDescription }"
      >
        <div class="i-event-item-expand__content" v-html="sanitizedEventDescription" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$infoAndActionWidth: 242px;

.i-event-item {
  border-radius: 20px;
  padding: 0;
  border: none;

  &__top {
    background: $secondary;
    border-radius: 20px 20px 0 0;
    padding: 6px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $dark-text;
    font-size: 14px;
  }

  &__wrapper {
    padding: 24px;
  }

  &_soon {
    border-top: none;
    border-radius: 0 0 20px 20px;
  }

  &__soon-block {
    text-transform: uppercase;
    background-color: $background;
    border: none;
    user-select: none;
    font-size: 12px;

    &:hover {
      color: $primary;
    }
  }

  &__package-name-wrapper {
    display: flex;
    flex-direction: column;
    margin-left: 12px;
  }

  &__nom-name {
    font-size: 14px;
    color: $grey-text;
  }

  &__name_package {
    color: $dark-text;
    font-size: 18px;
    font-weight: 700;
  }

  &__logo {
    display: flex;
		justify-content: center;
		align-items: center;
		width: 50px;
		height: 50px;
		min-width: 50px;
		min-height: 50px;

		img {
			object-fit: contain;
			width: 100%;
			height: 100%;
		}
  }

  &:not(.i-event-item_short) {
    .i-event-item {
      &-info {
        min-width: 242px;
        max-width: 242px;
      }

      &-actions {
        min-width: 310px;
        max-width: 310px;
      }
    }
  }

  @media (max-width: $screen-xs) {
    &__package-name-wrapper {
      gap: 8px;
      align-items: center;
      text-align: center;
      margin-left: 0;
    }

    &__wrapper {
      padding: 16px;
    }

    &:not(.i-event-item_short) {
      .i-event-item {
        &-info {
          min-width: 100%;
          max-width: 100%;
        }

        &-actions {
          min-width: 100%;
          max-width: 100%;
        }
      }
    }
  }
}

.i-event-item-info {
  gap: 8px;

  &__date {
    font-size: 16px;
  }

  &__text {
    font-size: 12px;
  }

  &__matches {
    margin: 0;
    background-color: $light-gray;
    border-radius: $border-radius;
    color: $primary;
    font-weight: 700;
    text-transform: uppercase;
  }

  &_home-logo {
    margin-right: 200px;
  }

  @media (max-width: $screen-xs) {
    margin-bottom: 16px;

    &_home-logo {
      margin-right: auto;
    }
  }
}

.i-event-item-actions {
  &__btn-buy {
    padding: 0 12px;
  }

  &__btn-more {
    padding: 0 24px;
    text-transform: uppercase;
  }

  &__parking {
    padding: 0 12px;
  }

  @media (max-width: $screen-xs) {
    display: flex;
    flex-wrap: nowrap;
    margin-top: 16px;

    &__btn-buy,
    &__btn-more,
    &__parking {
      width: 100%;
    }
  }
}
</style>
